import { groupArrayByKeys } from '@campus/utils';
import { Dictionary } from '@ngrx/entity';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  EduContent,
  EduContentTOCNavigationInterface,
  PersonInterface,
  SectionContentInterface,
  SectionInterface,
} from '../../+models';
import { EduContentTocNavigationFeature } from '../edu-content-toc-navigation';
import { UserQueries } from '../user';
import { getAllEntities as getEduContentDict } from './../edu-content/edu-content.selectors';
import { getBySectionId as getSectionContentsBySectionId } from './../section-content/section-content.selectors';
import { NAME, selectAll, selectEntities, selectIds, selectTotal, State } from './section.reducer';

export const selectSectionState = createFeatureSelector<State>(NAME);

export const getError = createSelector(selectSectionState, (state: State) => state.error);

export const getLoaded = createSelector(selectSectionState, (state: State) => state.loaded);

export const getAll = createSelector(selectSectionState, selectAll);

export const getCount = createSelector(selectSectionState, selectTotal);

export const getIds = createSelector(selectSectionState, selectIds);

export const getAllEntities = createSelector(selectSectionState, selectEntities);

export const getAllForTeacher = createSelector(getAll, (sections: SectionInterface[]): SectionInterface[] => {
  return sections.filter((section) => section.showTeacher);
});

export const getAllForStudent = createSelector(getAll, (sections: SectionInterface[]): SectionInterface[] => {
  return sections.filter((section) => section.showStudent);
});

/**
 * Returns sections filtered based on the current user's role.
 * If user has student role, returns sections visible to students.
 * If user has teacher role (but not student), returns sections visible to teachers.
 * Otherwise returns all sections.
 */
export const getAllForCurrentRole = createSelector(
  getAllForStudent,
  getAllForTeacher,
  getAll,
  UserQueries.getCurrentUser,
  (
    studentSections: SectionInterface[],
    teacherSections: SectionInterface[],
    allSections: SectionInterface[],
    user: PersonInterface
  ): SectionInterface[] => {
    const roles: string[] = user.roles.map((_role) => _role.name);

    if (roles.includes('student')) {
      return studentSections;
    } else if (roles.includes('teacher')) {
      return teacherSections;
    }

    return allSections;
  }
);

/**
 * returns array of objects in the order of the given ids
 * @example
 * section$: SectionInterface[] = this.store.pipe(
    select(SectionQueries.getByIds, { ids: [2, 1, 3] })
  );
 */
export const getByIds = createSelector(selectSectionState, (state: State, props: { ids: number[] }) => {
  return props.ids.map((id) => state.entities[id]);
});

/**
 * returns array of objects in the order of the given ids
 * @example
 * section$: SectionInterface = this.store.pipe(
    select(SectionQueries.getById, { id: 3 })
  );
 */
export const getById = createSelector(
  selectSectionState,
  (state: State, props: { id: number }) => state.entities[props.id]
);

/**
 * Factory function to create a selector that gets sections by eduContentTocId
 * based on a source selector for sections
 */
const createGetByEduContentTocIdSelector = (getSectionsSelector) =>
  createSelector(
    getSectionsSelector,
    getSectionContentsBySectionId,
    getEduContentDict,
    (
      sections: SectionInterface[],
      sectionContentsBySectionId: Dictionary<SectionContentInterface[]>,
      eduContentDict: Dictionary<EduContent>
    ): Dictionary<SectionInterface[]> => {
      const sectionsByEduContentTocId = groupArrayByKeys(sections, ['eduContentTOCId'], undefined, false, (section) => {
        const sectionContents = sectionContentsBySectionId[section.id] || [];

        const filteredSectionContents = sectionContents.filter((sectionContent) => {
          return eduContentDict[sectionContent.eduContentId]?.publishedEduContentMetadata?.showInDigitalBookTab;
        });

        return {
          ...section,
          sectionContents: filteredSectionContents,
        };
      });

      return sectionsByEduContentTocId;
    }
  );

export const getByEduContentTocIdForCurrentRole = createGetByEduContentTocIdSelector(getAllForCurrentRole);
export const getByEduContentTocIdForTeacher = createGetByEduContentTocIdSelector(getAllForTeacher);
export const getByEduContentTocIdForStudent = createGetByEduContentTocIdSelector(getAllForStudent);

/**
 * returns a dictionary of Sections grouped by eduContentTocId
 * @example
 * section$: Dictionary<SectionInterface[]> = this.store.pipe(
 *  select(SectionQueries.getSectionsByEduContentTocId)
 * );
 **/
export const getSectionsByEduContentTocId = createSelector(
  getAll,
  (sections: SectionInterface[]): Dictionary<SectionInterface[]> => {
    return groupArrayByKeys(sections, ['eduContentTOCId']);
  }
);

/**
 * Set of BOOK-LEVEL navigationIds that have at least one linked Section. Used to block
 * deleting a book navigation item while sections still reference it (mirrors the
 * server-side delete guard).
 *
 * A Section links to a navigation by its TOC-level id: `section.navigationId` ===
 * `EduContentTOCNavigation.externalId`. The book view renders book-level navigations,
 * so each link is resolved to its book-level id via `sourceNavigationId`. A navigation
 * without a source already is the book-level one; a link with no matching toc
 * navigation is kept as-is (it is already a book-level id).
 */
// Read the slice directly (rather than via the feature's entity `selectAll`) so the
// selector never throws when the `eduContentTOCNavigations` feature isn't registered in
// the host app's store — it degrades to "no mapping" instead of crashing the whole list.
const selectEduContentTOCNavigationState = createFeatureSelector<EduContentTocNavigationFeature.State>(
  EduContentTocNavigationFeature.NAME
);

export const getBookNavigationIdsWithSections = createSelector(
  getAll,
  selectEduContentTOCNavigationState,
  (sections: SectionInterface[], tocNavState: EduContentTocNavigationFeature.State | undefined): Set<number> => {
    const tocNavigations = tocNavState
      ? (Object.values(tocNavState.entities) as EduContentTOCNavigationInterface[])
      : [];

    const bookLevelIdByExternalId = new Map<number, number>();
    for (const navigation of tocNavigations) {
      if (navigation?.externalId != null) {
        bookLevelIdByExternalId.set(navigation.externalId, navigation.sourceNavigationId ?? navigation.externalId);
      }
    }

    const bookNavigationIds = new Set<number>();
    for (const section of sections) {
      if (section.navigationId != null) {
        bookNavigationIds.add(bookLevelIdByExternalId.get(section.navigationId) ?? section.navigationId);
      }
    }
    return bookNavigationIds;
  }
);

/** Groups the navigationIds that have at least one linked Section, keyed by `eduContentTOCId`. */
const groupNavigationIdsWithSectionsByToc = (sections: SectionInterface[]): Map<number, Set<number>> => {
  const byToc = new Map<number, Set<number>>();
  for (const section of sections) {
    if (section.eduContentTOCId != null && section.navigationId != null) {
      const ids = byToc.get(section.eduContentTOCId) ?? new Set<number>();
      ids.add(section.navigationId);
      byToc.set(section.eduContentTOCId, ids);
    }
  }
  return byToc;
};

/**
 * Per-toc set of override navigationIds that still have a linked Section, keyed by
 * `eduContentTOCId`. The toc navigation view shows a toc's OWN override items, which a
 * Section references directly (`section.navigationId` === the override's id). So unlike
 * {@link getBookNavigationIdsWithSections}, no `sourceNavigationId` resolution is needed
 * here — the ids are already in the same space as the rows the toc view renders.
 *
 * Used to block dropping a toc's override (toggling it off deletes all its items at
 * once, which the API rejects while sections still point at them) and to flag/lock the
 * affected rows.
 */
export const getNavigationIdsWithSectionsByToc = createSelector(getAll, groupNavigationIdsWithSectionsByToc);

/**
 * Same as {@link getNavigationIdsWithSectionsByToc} but only counts sections that are visible to
 * the current role (`section.showTeacher` / `section.showStudent`). Use this when deciding what
 * to render to the user — e.g. whether a custom navigation item has any sections to show — while
 * {@link getNavigationIdsWithSectionsByToc} stays role-agnostic for delete guards that must
 * consider every linked section.
 */
export const getNavigationIdsWithSectionsByTocForCurrentRole = createSelector(
  getAllForCurrentRole,
  groupNavigationIdsWithSectionsByToc
);

export const isBookLoaded = (props: { bookId: number }) =>
  createSelector(selectSectionState, (state: State) => !!state.loadedForBook[props.bookId]);

export const isTocLoaded = (props: { tocId: number }) =>
  createSelector(selectSectionState, (state: State) => !!state.loadedForToc[props.tocId]);
